import React from 'react';

import { formatIndianNumber } from '@utils/numberFormatter';

export interface LocalizedNumberProps {
  /** The number to display */
  value: number;
  /** Whether to display as currency (with ₹ symbol) */
  asCurrency?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show a tooltip with human-readable format on hover */
  showTooltip?: boolean;
  /** Custom tooltip content (overrides default human-readable format) */
  tooltipContent?: string;
}

/**
 * A component that displays numbers in standard localized format (e.g., 50,00,000)
 * with optional tooltips showing human-readable breakdown (e.g., "50 Lakh")
 */
export const LocalizedNumber: React.FC<LocalizedNumberProps> = ({
  value,
  asCurrency = false,
  className = '',
  showTooltip = true,
  tooltipContent,
}) => {
  // Get the main display value (standard localized format)
  const getDisplayValue = (): string => {
    const localized = value.toLocaleString('en-IN', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    return asCurrency ? `₹${localized}` : localized;
  };

  // Get tooltip content (human-readable format)
  const getTooltipContent = (): string => {
    if (tooltipContent) return tooltipContent;

    const humanReadable = formatIndianNumber(value);
    return asCurrency ? `₹${humanReadable}` : humanReadable;
  };

  const displayValue = getDisplayValue();

  if (showTooltip) {
    return (
      <span className={`relative group cursor-help ${className}`}>
        {displayValue}
        {/* Tooltip */}
        <span className="invisible group-hover:visible absolute z-10 px-3 py-2 text-sm text-white bg-gray-800 rounded-lg shadow-lg -top-12 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
          {getTooltipContent()}
          {/* Arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
        </span>
      </span>
    );
  }

  return <span className={className}>{displayValue}</span>;
};

/**
 * Preset components for common use cases
 */

// For displaying currency amounts with tooltips
export const LocalizedCurrency: React.FC<
  Omit<LocalizedNumberProps, 'asCurrency'>
> = props => <LocalizedNumber {...props} asCurrency={true} />;

// For displaying regular numbers with tooltips
export const LocalizedAmount: React.FC<LocalizedNumberProps> = props => (
  <LocalizedNumber {...props} />
);

export default LocalizedNumber;
